import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import api from '../../utils/api';
import { useAuth } from '../../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Switch } from '../ui/switch';
import { Label } from '../ui/label';
import { ArrowUpRight, CreditCard, Calendar } from 'lucide-react';
import { useToast } from '../../hooks/use-toast';
import SubscriptionStatusBadge from './SubscriptionStatusBadge';
import UsageMeter from './UsageMeter';
import UpgradePromoBanner from './UpgradePromoBanner';
import ModuleUpgradeDialog from './ModuleUpgradeDialog';
import { PlanFeaturesSection } from './SubscriptionSummaryCard';
import { formatBillingCycleLabel } from '../../utils/planPricing';

const USAGE_LABELS = {
  students: 'Students', teachers: 'Teachers', staff: 'Staff', parents: 'Parents',
  classes: 'Classes', sections: 'Sections',
};

export default function SchoolSubscription() {
  const { user, refreshUser } = useAuth();
  const { toast } = useToast();
  const [searchParams] = useSearchParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [upgradeDialog, setUpgradeDialog] = useState({ open: false, moduleKey: null, moduleLabel: null });

  const fetchData = async () => {
    try {
      const res = await api.get('subscription/current');
      setData(res.data?.data || null);
    } catch {
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    if (searchParams.get('success') === '1') {
      toast({ title: 'Payment successful', description: 'Your subscription is being activated.' });
      refreshUser?.();
    }
  }, []);

  const toggleAutoRenew = async (checked) => {
    try {
      await api.put('subscription/auto-renew', { auto_renew: checked });
      fetchData();
      toast({ title: checked ? 'Auto-renew enabled' : 'Auto-renew disabled' });
    } catch (e) {
      toast({ title: 'Error', description: e.response?.data?.message, variant: 'destructive' });
    }
  };

  if (loading) return <p className="text-muted-foreground">Loading subscription…</p>;

  const sub = data?.subscription;
  const summary = data?.summary || user?.subscription || {};

  return (
    <div className="space-y-6 max-w-4xl">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold">Subscription</h1>
          <p className="text-muted-foreground">Manage your school plan and usage</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" asChild><Link to="/settings/subscription/payments"><CreditCard className="w-4 h-4 mr-2" />Payment History</Link></Button>
          <Button className="bg-blue-600" asChild><Link to="/settings/subscription/upgrade"><ArrowUpRight className="w-4 h-4 mr-2" />Upgrade Plan</Link></Button>
        </div>
      </div>

      <UpgradePromoBanner user={user} />

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>{sub?.plan?.name || summary.plan_name || 'No plan'}</CardTitle>
            <SubscriptionStatusBadge status={sub?.status || summary.status} />
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 text-sm">
            <div>
              <p className="text-muted-foreground text-xs mb-1">Current Plan</p>
              <p className="font-medium">{sub?.plan?.name || summary.plan_name || 'No plan'}</p>
            </div>
            <div>
              <p className="text-muted-foreground text-xs mb-1">Billing Cycle</p>
              <p className="font-medium capitalize">
                {formatBillingCycleLabel(sub?.billing_cycle || summary.billing_cycle || '—')}
              </p>
            </div>
            <div className="flex items-center gap-2">
              <Calendar className="w-4 h-4 text-muted-foreground shrink-0" />
              <div>
                <p className="text-muted-foreground text-xs">Expires</p>
                <p className="font-medium">{sub?.expires_at ? new Date(sub.expires_at).toLocaleDateString() : '—'}</p>
              </div>
            </div>
            <div>
              <p className="text-muted-foreground text-xs mb-1">Days Remaining</p>
              <p className="font-medium">{summary.days_remaining ?? 0} days</p>
            </div>
          </div>
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Calendar className="w-4 h-4 shrink-0" />
            <span>Started: {sub?.starts_at ? new Date(sub.starts_at).toLocaleDateString() : '—'}</span>
          </div>
          <div className="flex items-center gap-3">
            <Switch checked={!!sub?.auto_renew} onCheckedChange={toggleAutoRenew} id="auto-renew" />
            <Label htmlFor="auto-renew">Auto renewal (informational)</Label>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle>Usage</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          {Object.entries(summary.usage || {}).map(([key, row]) => (
            <UsageMeter key={key} label={USAGE_LABELS[key] || key} used={row.used} limit={row.limit} />
          ))}
          {(summary.limits_reached || []).length > 0 && (
            <p className="text-sm text-red-600 font-medium">
              Limits reached: {(summary.limits_reached || []).join(', ')}. Please upgrade your plan.
            </p>
          )}
        </CardContent>
      </Card>

      <PlanFeaturesSection
        user={user}
        onUpgradeModule={(m) => setUpgradeDialog({ open: true, moduleKey: m.key, moduleLabel: m.label })}
      />

      <ModuleUpgradeDialog
        open={upgradeDialog.open}
        moduleKey={upgradeDialog.moduleKey}
        moduleLabel={upgradeDialog.moduleLabel}
        onClose={() => setUpgradeDialog({ open: false, moduleKey: null, moduleLabel: null })}
      />
    </div>
  );
}
